import { useEffect, useState } from 'react';
import { Sparkles, Plus, Loader2, Pencil, Trash2, X, Eye, EyeOff, ArrowUp, ArrowDown, Image, Film } from 'lucide-react';
import { apiFetch } from '../lib/api';
import MediaLibraryPicker from '../components/admin/MediaLibraryPicker';

interface UpsellSlide {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  imageUrl: string;
  videoUrl?: string;
  sortOrder: number;
  isActive: boolean;
  zoomEffect?: boolean;
}

const emptyForm: Omit<UpsellSlide, 'id'> = {
  title: '',
  subtitle: '',
  description: '',
  imageUrl: '',
  videoUrl: '',
  sortOrder: 0,
  isActive: true,
  zoomEffect: true,
};

function generateId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

export default function AdminAhJimUpsell() {
  const [slides, setSlides] = useState<UpsellSlide[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<Omit<UpsellSlide, 'id'>>(emptyForm);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerType, setPickerType] = useState<'image' | 'video'>('image');

  const fetchSlides = async () => {
    setLoading(true);
    const res = await apiFetch<UpsellSlide[]>('/api/admin/upsell-slides');
    if (res.success && res.data) {
      const sorted = [...res.data].sort((a, b) => a.sortOrder - b.sortOrder);
      setSlides(sorted);
    }
    setLoading(false);
    setHasChanges(false);
  };

  useEffect(() => {
    fetchSlides();
  }, []);

  const handleSaveAll = async () => {
    const payload = slides.map((s, idx) => ({ ...s, sortOrder: idx + 1 }));
    setSaving(true);
    const res = await apiFetch('/api/admin/upsell-slides', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    setSaving(false);
    if (res.success) {
      setHasChanges(false);
      alert('幻燈片已保存');
      fetchSlides();
    } else {
      alert(res.error?.message || '保存失敗');
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm, sortOrder: slides.length + 1 });
    setModalOpen(true);
  };

  const openEdit = (slide: UpsellSlide) => {
    setEditingId(slide.id);
    setForm({
      title: slide.title,
      subtitle: slide.subtitle,
      description: slide.description,
      imageUrl: slide.imageUrl,
      videoUrl: slide.videoUrl || '',
      sortOrder: slide.sortOrder,
      isActive: slide.isActive,
      zoomEffect: slide.zoomEffect !== false,
    });
    setModalOpen(true);
  };

  const handleSaveSlide = () => {
    if (!form.title.trim()) { alert('請輸入標題'); return; }
    if (editingId) {
      setSlides((prev) => prev.map((s) => (s.id === editingId ? { ...form, id: editingId } : s)));
    } else {
      setSlides((prev) => [...prev, { ...form, id: generateId() }]);
    }
    setHasChanges(true);
    setModalOpen(false);
  };

  const handleDelete = (id: string) => {
    if (!confirm('確定要刪除這張幻燈片嗎？')) return;
    setSlides((prev) => prev.filter((s) => s.id !== id));
    setHasChanges(true);
  };

  const toggleActive = (id: string) => {
    setSlides((prev) => prev.map((s) => (s.id === id ? { ...s, isActive: !s.isActive } : s)));
    setHasChanges(true);
  };

  const moveSlide = (index: number, direction: -1 | 1) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= slides.length) return;
    setSlides((prev) => {
      const next = [...prev];
      const [item] = next.splice(index, 1);
      next.splice(newIndex, 0, item);
      return next;
    });
    setHasChanges(true);
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-brand-600" />
            阿占Upsell
          </h1>
          <div className="flex items-center gap-2">
            {hasChanges && (
              <span className="text-sm text-amber-600 font-medium">有未保存的更改</span>
            )}
            <button onClick={handleSaveAll} disabled={saving} className="btn-primary py-2.5 px-5 min-h-[44px]">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {saving ? '保存中...' : '保存全部'}
            </button>
            <button onClick={openCreate} className="btn-secondary py-2.5 px-5 min-h-[44px]">
              <Plus className="w-4 h-4" />
              新增幻燈片
            </button>
          </div>
        </div>
        <p className="text-sm text-gray-500 mt-1">管理捐款 Upsell 輪播幻燈片內容與排序</p>
      </div>

      <div className="card shadow-sm border border-brand-100">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-gray-500">
                <th className="px-4 py-3 text-left font-medium w-16">預覽</th>
                <th className="px-4 py-3 text-left font-medium">標題</th>
                <th className="px-4 py-3 text-left font-medium">副標題</th>
                <th className="px-4 py-3 text-left font-medium">描述</th>
                <th className="px-4 py-3 text-center font-medium w-20">縮放</th>
                <th className="px-4 py-3 text-center font-medium w-24">排序</th>
                <th className="px-4 py-3 text-center font-medium w-16">狀態</th>
                <th className="px-4 py-3 text-right font-medium w-28">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 7 }).map((_, j) => (
                      <td key={j} className="px-4 py-3"><span className="inline-block w-full h-4 bg-gray-100 rounded animate-pulse" /></td>
                    ))}
                  </tr>
                ))
              ) : slides.length > 0 ? (
                slides.map((slide, idx) => (
                  <tr key={slide.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      {slide.videoUrl ? (
                        <div className="w-12 aspect-[4/5] rounded-lg bg-gray-900 flex items-center justify-center text-white relative overflow-hidden">
                          <Film className="w-5 h-5" />
                          <div className="absolute bottom-0.5 right-0.5 w-2 h-2 bg-red-500 rounded-full" />
                        </div>
                      ) : slide.imageUrl ? (
                        <img src={slide.imageUrl} alt={slide.title} className="w-12 aspect-[4/5] object-cover rounded-lg border border-gray-200" loading="lazy" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                      ) : (
                        <div className="w-12 aspect-[4/5] rounded-lg bg-gray-100 flex items-center justify-center text-gray-400"><Image className="w-5 h-5" /></div>
                      )}
                    </td>
                    <td className="px-4 py-3"><div className="font-medium text-gray-900">{slide.title}</div></td>
                    <td className="px-4 py-3 text-gray-600">{slide.subtitle}</td>
                    <td className="px-4 py-3"><div className="text-gray-600 line-clamp-2 max-w-[280px]">{slide.description}</div></td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${slide.zoomEffect !== false ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                        {slide.zoomEffect !== false ? '開啟' : '關閉'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => moveSlide(idx, -1)} disabled={idx === 0} className="p-1.5 text-gray-500 hover:text-brand-600 hover:bg-brand-50 rounded-md transition-colors disabled:opacity-30 disabled:hover:bg-transparent" title="上移"><ArrowUp className="w-4 h-4" /></button>
                        <span className="text-xs font-medium text-gray-500 w-5">{idx + 1}</span>
                        <button onClick={() => moveSlide(idx, 1)} disabled={idx === slides.length - 1} className="p-1.5 text-gray-500 hover:text-brand-600 hover:bg-brand-50 rounded-md transition-colors disabled:opacity-30 disabled:hover:bg-transparent" title="下移"><ArrowDown className="w-4 h-4" /></button>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button onClick={() => toggleActive(slide.id)} className={`inline-flex items-center justify-center w-8 h-8 rounded-lg transition-colors ${slide.isActive ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'}`} title={slide.isActive ? '顯示中' : '已隱藏'}>
                        {slide.isActive ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openEdit(slide)} className="p-2 text-gray-500 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors" title="編輯"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => handleDelete(slide.id)} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="刪除"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-gray-400">
                    暫無幻燈片，點擊「新增幻燈片」開始添加
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Slide Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setModalOpen(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">{editingId ? '編輯幻燈片' : '新增幻燈片'}</h2>
              <button onClick={() => setModalOpen(false)} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">標題 *</label>
                <input type="text" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm" placeholder="輸入幻燈片標題..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">副標題</label>
                <input type="text" value={form.subtitle} onChange={(e) => setForm((f) => ({ ...f, subtitle: e.target.value }))} className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm" placeholder="輸入副標題..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">描述</label>
                <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={3} className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm" placeholder="輸入描述內容..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">圖片 / 影片</label>
                <div className="flex items-center gap-3">
                  <div className="space-y-1">
                    <span className="text-xs text-gray-500">圖片</span>
                    {form.imageUrl ? (
                      <img src={form.imageUrl} alt="preview" className="w-20 aspect-[4/5] object-cover rounded-xl border border-gray-200" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                    ) : (
                      <div className="w-20 aspect-[4/5] rounded-xl bg-gray-100 flex items-center justify-center text-gray-400 border border-gray-200"><Image className="w-6 h-6" /></div>
                    )}
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => { setPickerType('image'); setPickerOpen(true); }} className="btn-secondary py-1.5 px-3 text-xs">{form.imageUrl ? '更換' : '選擇'}</button>
                      {form.imageUrl && <button type="button" onClick={() => setForm((f) => ({ ...f, imageUrl: '' }))} className="text-xs text-red-500 hover:text-red-600">清除</button>}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <span className="text-xs text-gray-500">影片（優先顯示）</span>
                    {form.videoUrl ? (
                      <div className="w-20 aspect-[4/5] rounded-xl bg-gray-900 flex items-center justify-center text-white relative overflow-hidden border border-gray-200"><Film className="w-6 h-6" /><div className="absolute bottom-1 right-1 w-2.5 h-2.5 bg-red-500 rounded-full" /></div>
                    ) : (
                      <div className="w-20 aspect-[4/5] rounded-xl bg-gray-100 flex items-center justify-center text-gray-400 border border-gray-200"><Film className="w-6 h-6" /></div>
                    )}
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => { setPickerType('video'); setPickerOpen(true); }} className="btn-secondary py-1.5 px-3 text-xs">{form.videoUrl ? '更換' : '選擇'}</button>
                      {form.videoUrl && <button type="button" onClick={() => setForm((f) => ({ ...f, videoUrl: '' }))} className="text-xs text-red-500 hover:text-red-600">清除</button>}
                    </div>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">排序</label>
                  <input type="number" min={1} value={form.sortOrder} onChange={(e) => setForm((f) => ({ ...f, sortOrder: Number(e.target.value) }))} className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm" />
                </div>
                <div className="flex items-end pb-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.isActive} onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))} className="w-4 h-4 text-brand-600 border-gray-300 rounded focus:ring-brand-500" />
                    <span className="text-sm text-gray-700">前台顯示</span>
                  </label>
                </div>
                <div className="flex items-end pb-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.zoomEffect !== false} onChange={(e) => setForm((f) => ({ ...f, zoomEffect: e.target.checked }))} className="w-4 h-4 text-brand-600 border-gray-300 rounded focus:ring-brand-500" />
                    <span className="text-sm text-gray-700">縮放效果</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
              <button onClick={() => setModalOpen(false)} className="btn-secondary py-2.5 px-5 min-h-[44px]">取消</button>
              <button onClick={handleSaveSlide} className="btn-primary py-2.5 px-5 min-h-[44px]">保存</button>
            </div>
          </div>
        </div>
      )}

      <MediaLibraryPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        filterType={pickerType}
        onSelect={(url) => {
          if (pickerType === 'video') setForm((f) => ({ ...f, videoUrl: url }));
          else setForm((f) => ({ ...f, imageUrl: url }));
          setPickerOpen(false);
        }}
      />
    </div>
  );
}
